<template>
  <ul ref="root" class="sec-stories" :aria-label="label">
    <li v-for="story in items" :key="story.route" class="sec-stories__item">
      <a class="sec-story" :href="story.route" @click.prevent="navigate(story.route)">
        <span class="sec-story__media">
          <img :src="story.image" alt="" width="1040" height="430" loading="lazy" />
        </span>
        <span class="sec-story__body">
          <span class="sec-story__meta">
            <span class="m600 sec-story__tag">{{ story.tag }}</span>
            <img class="sec-story__logo" :src="story.logo" :alt="story.company" height="36" />
          </span>
          <h3 class="o600 sec-story__title">{{ story.title }}</h3>
          <span class="m500 sec-story__text">{{ story.description }}</span>
          <span class="m600 sec-story__more">
            {{ readMore }}
            <v-icon size="18" aria-hidden="true">mdi-arrow-right</v-icon>
          </span>
        </span>
      </a>
    </li>
  </ul>
</template>

<script setup>
// Customer story cards: cover image, tag and logo, title, one-line result, "Read more".
// The whole card is one link. Columns: 1 on phones, 2 from 640px, 3 from 1024px; rows wrap
// and centre, so a short last row sits in the middle.
// Motion (GSAP, via useMotion): when the grid scrolls into view the cards rise in one after
// another while each cover settles from a slight zoom. Hover (CSS): the card lifts, the cover
// zooms gently and the arrow slides. Reduced motion shows everything at rest.
import { ref } from 'vue';
import { gsap } from '@/plugins/gsap';
import { useMotion } from '@/composables/security/useMotion';
import { useSmoothNavigate } from '@/composables/security/useSmoothScroll';

defineProps({
  items: { type: Array, required: true }, // [{ company, tag, title, description, image, logo, route }]
  label: { type: String, default: '' },
  readMore: { type: String, default: 'Read more' },
});

const root = ref(null);
const navigate = useSmoothNavigate();

useMotion(root, (el) => {
  const cards = gsap.utils.toArray('.sec-stories__item', el);
  const covers = gsap.utils.toArray('.sec-story__media img', el);
  gsap
    .timeline({ scrollTrigger: { trigger: el, start: 'top 85%', once: true } })
    .from(cards, {
      autoAlpha: 0,
      y: 56,
      scale: 0.96,
      duration: 1,
      ease: 'expo.out',
      stagger: 0.14,
      clearProps: 'transform,opacity,visibility',
    })
    .from(
      covers,
      { scale: 1.15, duration: 1.4, ease: 'expo.out', stagger: 0.14, clearProps: 'transform' },
      '<'
    );
});
</script>

<style scoped>
.sec-stories {
  --cols: 1;
  --gap: var(--sec-s3);
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--sec-s4) var(--gap);
}

.sec-stories__item {
  display: flex;
  flex: 0 0 calc((100% - (var(--cols) - 1) * var(--gap)) / var(--cols));
  min-width: 0;
}

.sec-story {
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  text-align: left;
  text-decoration: none;
  background: var(--sec-surface);
  border: 1px solid var(--sec-line);
  border-radius: 16px;
  box-shadow: 0 2px 6px -2px var(--sec-header-shadow);
  transition: transform 0.45s cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 0.45s ease, border-color 0.3s ease;
}

.sec-story:hover,
.sec-story:focus-visible {
  transform: translateY(-6px);
  border-color: var(--sec-accent);
  box-shadow: 0 24px 40px -20px var(--sec-header-shadow);
}

.sec-story__media {
  display: block;
  overflow: hidden;
  aspect-ratio: 1040 / 430;
  background: var(--sec-surface-2);
}

.sec-story__media img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.8s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.sec-story:hover .sec-story__media img,
.sec-story:focus-visible .sec-story__media img {
  transform: scale(1.05);
}

.sec-story__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.25rem 1.25rem 1.5rem;
}

.sec-story__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sec-s2);
}

.sec-story__tag {
  padding: 5px 12px;
  border-radius: 999px;
  background: var(--sec-accent-subtle);
  color: var(--sec-accent-text);
  font-size: 0.75rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.sec-story__logo {
  display: block;
  height: 32px;
  width: auto;
  max-width: 120px;
  object-fit: contain;
}

.sec-story__title {
  margin: 0.25rem 0 0;
  font-size: 1.3rem;
  line-height: 1.3;
  color: var(--sec-ink);
  transition: color 0.3s ease;
}

.sec-story:hover .sec-story__title {
  color: var(--sec-accent-text);
}

.sec-story__text {
  flex: 1;
  font-size: 1rem;
  line-height: 1.6;
  color: var(--sec-ink-2);
}

.sec-story__more {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 0.25rem;
  font-size: 0.95rem;
  color: var(--sec-accent-text);
}

.sec-story__more .v-icon {
  transition: transform 0.3s ease;
}

.sec-story:hover .sec-story__more .v-icon {
  transform: translateX(4px);
}

@media only screen and (min-width: 640px) {
  .sec-stories {
    --cols: 2;
  }
}

@media only screen and (min-width: 1024px) {
  .sec-stories {
    --cols: 3;
    --gap: var(--sec-s4);
  }
  .sec-story__body {
    padding: 1.5rem 1.5rem 1.75rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sec-story,
  .sec-story__media img,
  .sec-story__title,
  .sec-story__more .v-icon {
    transition: none;
  }
  .sec-story:hover,
  .sec-story:hover .sec-story__media img,
  .sec-story:hover .sec-story__more .v-icon {
    transform: none;
  }
}
</style>
